import {
	forwardRef,
	MouseEvent,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useRef,
} from "react";
import classNames from "classnames";
import { Anchor } from "../Anchor/Anchor";
import { CollapsibleMenu } from "../CollapsibleMenu/CollapsibleMenu";
import { CollapsibleListItemContext, CollapsibleListItemRenderMode } from "../CollapsibleList/CollapsibleList";
import { localize } from "client_legacy/ndaw/culture";
import {
	FontIconName,
	Icon,
	IconValue,
	SvgIconValue
} from "../Icon/Icon";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { ActionListItem } from "../ActionList/ActionListItem";
import styles from "./TabsNavigation.module.less";

export type TabsNavigationItemData<TValue> = {
	value: TValue;
	text: string;
	title?: string;
	icon?: FontIconName | SvgIconValue;
	rightIcon?: IconValue;
	trackingId?: string;
	isDisabled?: boolean;
};

export type TabsNavigationProps<TValue> = {
	value: TValue | undefined;
	onChange(newValue: TValue): void;
	items: readonly TabsNavigationItemData<TValue>[];
	trackingId?: string;
	height?: "auto" | "fill";
	hasUnstableContainer?: boolean;
	onMaxContentWidthChange?(maxContentWidth: number): void;
	areEqual?(value1: TValue, value2: TValue | undefined): boolean;
};

export const TabsNavigation = <TValue, >(
	props: TabsNavigationProps<TValue>
): ReactElement => {
	const {
		items,
		value,
		onChange,
		trackingId,
		height = "auto",
		hasUnstableContainer = false,
		onMaxContentWidthChange,
		areEqual: areEqualFromProps
	} = props;

	const areEqual = useCallback(
		(value1: TValue, value2: TValue | undefined): boolean => {
			if (areEqualFromProps) {
				return areEqualFromProps(value1, value2);
			}

			return value1 === value2;
		},
		[areEqualFromProps]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root, {
					[styles.root_fill]: height === "fill",
				})}
		>
			<CollapsibleMenu
				moreButtonFactory={
					(
						isActive,
						tabIndex,
						onIsActiveChange
					): ReactElement => (
						<MoreButton
							onClick={() => onIsActiveChange(!isActive)}
							trackingId="more-button"
							tabIndex={tabIndex}
						/>
					)
				}
				trackingId={generateTrackingId(trackingId, "__responsive-list")}
				shouldOnlyMeasure={hasUnstableContainer}
				onMaxContentWidthChange={onMaxContentWidthChange}
				height="fill"
			>
				{items.map((item, index) => (
					<Item
						key={index}
						text={item.text}
						title={item.title}
						isActive={areEqual(item.value, value)}
						onActivate={() => onChange(item.value)}
						icon={item.icon}
						rightIcon={item.rightIcon}
						trackingId={item.trackingId}
						isDisabled={item.isDisabled}
					/>
				))}
			</CollapsibleMenu>
		</div>
	);
};
TabsNavigation.displayName = "TabsNavigation";

type ItemProps = {
	text: string;
	title?: string;
	isActive: boolean;
	onActivate(): void;
	icon?: FontIconName | SvgIconValue;
	rightIcon?: IconValue;
	trackingId?: string;
	isDisabled?: boolean;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		isActive,
		onActivate,
		text,
		title,
		icon,
		rightIcon,
		trackingId,
		isDisabled
	} = props;

	const {
		renderMode,
		onResponsiveBehaviorChange,
		resetResponsiveBehavior
	} = useContext(CollapsibleListItemContext);

	useEffect(
		(): EffectWithDestructorResult => {
			onResponsiveBehaviorChange(
				isActive
					? "always-visible"
					: "collapse"
			);

			return resetResponsiveBehavior;
		},
		[
			isActive,
			onResponsiveBehaviorChange,
			resetResponsiveBehavior
		]
	);

	switch (renderMode) {
		case CollapsibleListItemRenderMode.Normal:
			return (
				<RegularItem
					isActive={isActive}
					onActivate={onActivate}
					text={text}
					title={title}
					icon={icon}
					rightIcon={rightIcon}
					isDisabled={isDisabled}
					trackingId={trackingId}
				/>
			);
		case CollapsibleListItemRenderMode.Collapsed:
			return (
				<ActionListItem
					trackingId={trackingId}
					onClick={onActivate}
					text={text}
					title={title}
					icon={icon}
					rightIcon={rightIcon}
					isDisabled={isDisabled}
				/>
			);
		default:
			return never(renderMode);
	}

};

Item.displayName = "Item";

type RegularItemProps = {
	text: string;
	title?: string;
	isActive: boolean;
	onActivate(): void;
	icon?: FontIconName | SvgIconValue;
	rightIcon?: IconValue;
	trackingId?: string;
	isDisabled?: boolean;
};

const RegularItem = (props: RegularItemProps): ReactElement => {
	const {
		isActive,
		onActivate,
		text,
		title,
		icon,
		rightIcon,
		trackingId,
		isDisabled
	} = props;

	const anchorClickHandler = useCallback(
		(event: MouseEvent): void => {
			event.preventDefault();
			onActivate();
		},
		[onActivate]
	);

	return (
		<Anchor
			trackingId={trackingId}
			onClick={anchorClickHandler}
			className={classNames(
				styles.item,
				{
					[styles.item_active]: isActive,
					[styles.item_disabled]: isDisabled
				}
			)}
			title={title}
		>
			<div className={styles.textAndIcons}>
				{icon && (
					<Icon value={icon} />
				)}

				<span className={styles.text}>
					{text}
				</span>

				{rightIcon && (
					<Icon value={rightIcon} />
				)}
			</div>
		</Anchor>
	);
};
RegularItem.displayName = "RegularItem";

type MoreButtonProps = {
	onClick(): void;
	trackingId: string;
	tabIndex: number;
};

const MoreButton = forwardRef((props: MoreButtonProps, ref: Ref<HTMLElement>): ReactElement => {
	const {
		onClick,
		trackingId,
		tabIndex
	} = props;

	return (
		<Anchor
			trackingId={trackingId}
			onClick={onClick}
			className={styles.item}
			ref={ref as Ref<HTMLAnchorElement>}
			tabIndex={tabIndex}
		>
			<span className={styles.textAndIcons}>
				<span className={styles.text}>
					{localize("_More")}
				</span>

				<Icon value="chevron-down" />
			</span>
		</Anchor>
	);
});

MoreButton.displayName = "MoreButton";

export type ScrollableTabsNavigationProps<TValue> = {
	value: TValue | undefined;
	onChange(newValue: TValue): void;
	items: readonly TabsNavigationItemData<TValue>[];
	trackingId?: string;
	height?: "auto" | "fill";
	areEqual?(value1: TValue, value2: TValue | undefined): boolean;
};

export const ScrollableTabsNavigation = <TValue, >(
	props: ScrollableTabsNavigationProps<TValue>
) => {
	const {
		items,
		onChange,
		trackingId,
		value,
		height = "auto",
		areEqual: areEqualFromProps
	} = props;

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const areEqual = useCallback(
		(value1: TValue, value2: TValue | undefined): boolean => {
			if (areEqualFromProps) {
				return areEqualFromProps(value1, value2);
			}

			return value1 === value2;
		},
		[areEqualFromProps]
	);

	useEffect(
		(): void => {
			const { current } = ref;

			const activeIndex: number = items.findIndex((item) => areEqual(item.value, value));

			if (activeIndex > -1) {
				current?.children[activeIndex].scrollIntoView({
					behavior: "smooth",
				});
			}
		},
		[
			areEqual,
			items,
			value
		]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				styles.root_scrollable, {
					[styles.root_fill]: height === "fill",
				})}
			ref={ref}
		>
			{items.map((item, index) => (
				<RegularItem
					key={index}
					text={item.text}
					isActive={areEqual(item.value, value)}
					onActivate={() => onChange(item.value)}
					trackingId={item.trackingId}
					isDisabled={item.isDisabled}
				/>
			))}
		</div>
	);
};
ScrollableTabsNavigation.displayName = "ScrollableTabsNavigation";
